import React, { useEffect, useState } from 'react';
import Loader from '../Loader/Loader';
import Toast from '../Toast/Toast';
import { logoUrl } from './runtime';
import { INVALID_LINK, PHONE_MAX_PX } from '../constants';
import { useDevice } from '../hooks/useDevice';
import { useProgress } from './hooks/useProgress';
import { useWeddingDay } from './state/useWeddingDay';
import ProgressBand from './components/ProgressBand';
import BriefCard from './components/BriefCard';
import BottomBar from './components/BottomBar';
import SubmitGate from './components/SubmitGate';
import Section from './sections/Section';
import SubmitCard from './sections/SubmitCard';

const e = React.createElement;

/**
 * The form itself, rendered from whatever the store holds. It does not know
 * where the payload came from: the couple's own link and a staff read
 * bootstrap into the same slice. `banner` is the one thing the caller owns.
 */
const Shell = ({ banner, navSelector = '.navbar', staff, onSubmitted }) => {
  const {
    doc, loading, locked, sharedLocked, readOnly, canEdit, closed, denied,
    deniedCode, section, nextKey, saveState, toast, dismissToast, invalidLink,
    loadFailed,
  } = useWeddingDay();
  const { positionLabel } = useProgress();
  const { isPhone, narrow } = useDevice(PHONE_MAX_PX);
  const [gateOpen, setGateOpen] = useState(false);

  // The app Navbar has an auto height, so measure it rather than hardcoding an
  // offset that drifts between EG and WG chrome, and between the two apps.
  useEffect(() => {
    const nav = document.querySelector(navSelector);
    const sync = () => {
      if (nav) document.documentElement.style.setProperty('--wd-nav', `${nav.offsetHeight}px`);
    };
    sync();
    // The navbar's own height changes without the window resizing, on phone
    // where its items wrap, and a stale offset leaves a strip above the form.
    const observer = nav && window.ResizeObserver ? new ResizeObserver(sync) : null;
    if (observer) observer.observe(nav);
    window.addEventListener('resize', sync);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', sync);
    };
  }, [navSelector]);

  if (loading) return e(Loader, { logoSrc: logoUrl(), label: 'Loading your wedding day details' });

  // A broken link has no form behind it, so the toast is all there is. No
  // onClose, so it stays rather than leaving an empty page behind it.
  if (invalidLink) return e(Toast, { message: INVALID_LINK });

  // Nothing came back to render. Said plainly with a way to try again,
  // rather than an empty form that reads as nothing to fill in.
  if (loadFailed) {
    return e(
      'div',
      { className: 'wd-access' },
      e('div', { className: 'wd-access-box' }, [
        e('h1', { className: 'wd-access-head', key: 'h' },
          'We could not load your wedding day details'),
        e('p', { className: 'wd-access-body', key: 'b' },
          'Check your connection and try again. Your answers are safe.'),
        e('button', {
          key: 'retry',
          type: 'button',
          className: 'wd-btn wd-btn-brand',
          onClick: () => window.location.reload(),
        }, 'Try again'),
      ])
    );
  }

  // Refused by the server, so there is nothing to render but the reason, in
  // the same card the creative preferences form shows. The server answers
  // invalid_brief to a professional not on the booking as well, so in the
  // professional app that reads as not assigned.
  if (denied) {
    const notAssigned = deniedCode === 'brief_not_assigned' || (staff && deniedCode === 'invalid_brief');
    return e(
      'div',
      { className: 'wd-access' },
      e('div', { className: 'wd-access-box' }, [
        e(
          'svg',
          {
            key: 'art',
            className: 'wd-access-art',
            width: '26',
            height: '26',
            viewBox: '0 0 24 24',
            fill: 'none',
            stroke: 'currentColor',
            strokeWidth: '2',
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
            'aria-hidden': 'true',
          },
          [
            e('rect', { key: 'r', x: '3', y: '11', width: '18', height: '11', rx: '2' }),
            e('path', { key: 'p', d: 'M7 11V7a5 5 0 0 1 10 0v4' }),
          ]
        ),
        e(
          'h1',
          { className: 'wd-access-head', key: 'h' },
          notAssigned
            ? 'You are not assigned to this booking'
            // Only wedding bookings have a wedding day to plan.
            : deniedCode === 'no_wedding'
            ? 'This booking has no wedding day'
            : deniedCode === 'no_published_form'
            ? 'This form is not ready yet'
            : 'Only the couple can open this'
        ),
        notAssigned
          ? e(
              'p',
              { className: 'wd-access-body', key: 'b' },
              'This wedding day form is visible only to the crew assigned to the booking.'
            )
          : null,
        e(
          'ul',
          { className: 'wd-access-points', key: 'p' },
          notAssigned
            ? [
                e('li', { key: 1 }, 'A professional can open a form only for the bookings they are assigned to.'),
                e('li', { key: 2 }, 'Assignments are set by your coordinator on the booking itself.'),
                e('li', { key: 3 }, 'If you should be on this shoot, contact your coordinator to be assigned to it.'),
              ]
            : deniedCode === 'no_wedding'
            ? [
                e('li', { key: 1 }, 'This form covers wedding events, and this booking has none on it.'),
                e('li', { key: 2 }, 'If that looks wrong, contact your coordinator.'),
              ]
            : deniedCode === 'no_published_form'
            ? [
                e('li', { key: 1 }, 'The wedding day form has not been published yet.'),
                e('li', { key: 2 }, 'Please check again later, or contact your coordinator.'),
              ]
            : [
                e('li', { key: 1 }, 'This wedding day form is visible only to the bride and groom of the booking.'),
                e('li', { key: 2 }, 'If you think you should have access, contact your coordinator.'),
              ]
        ),
      ])
    );
  }

  const notice = doc.submitted
    ? 'Submitted. The form is closed now, apart from your own surprises. Ask your coordinator if anything has to change.'
    // Past the deadline the couple loses edit too, and that is not the
    // same as never having had it.
    : closed && !staff
    ? 'This form closed 30 days before your first event. Your coordinator can reopen it.'
    : readOnly
    ? canEdit === false && !staff
      ? 'Only the bride and groom can fill this form. You have view only access to this booking.'
      : 'Read only view of the client’s form.'
    : sharedLocked
    ? 'Your partner has submitted, so the form is closed. Your own surprises are still open, and you can still submit your side.'
    : null;

  return e(
    'div',
    {
      className: `wd-screen${isPhone ? ' is-phone' : ''}${narrow ? ' is-narrow' : ''}${
        locked ? ' is-locked' : ''
      }`,
    },
    [
      // Above the content on both, so the steps read as a header rather than
      // as a column stealing width the phone does not have.
      e(ProgressBand, { key: 'rail', isPhone }),
      e('div', { className: 'wd-body', key: 'body' }, [
        e('main', { className: 'wd-content', key: 'main' }, [
          staff ? null : e(BriefCard, { key: 'brief' }),
          banner,
          notice ? e('div', { className: 'wd-readonly', key: 'notice' }, notice) : null,
          saveState === 'error'
            ? e('div', { className: 'wd-savewarn', key: 'retry' }, 'Not saved yet, retrying.')
            : null,
          saveState === 'failed'
            ? e('div', { className: 'wd-savewarn', key: 'failed' }, 'Could not save your changes. Please refresh the page.')
            : null,
          saveState === 'rejected'
            ? e(
                'div',
                { className: 'wd-savewarn', key: 'rejected' },
                'We could not save that. Please refresh, or contact your coordinator.'
              )
            : null,
          toast ? e(Toast, { key: 'toast', message: toast, onClose: dismissToast }) : null,
          e(Section, {
            key: 'section',
            section,
            isPhone,
            footer:
              section && !nextKey ? e(SubmitCard, { onOpenSubmit: () => setGateOpen(true) }) : null,
          }),
        ]),
      ]),
      e(BottomBar, { key: 'bar' }),
      e(SubmitGate, {
        key: 'gate',
        open: gateOpen,
        onClose: () => setGateOpen(false),
        onSubmit: () => {
          setGateOpen(false);
          // The host decides where the couple goes once the form is in.
          if (onSubmitted) onSubmitted();
        },
      }),
      e(
        'div',
        { className: 'wd-sr-live', key: 'live', 'aria-live': 'polite' },
        doc.submitted ? 'Wedding day details submitted.' : positionLabel
      ),
    ]
  );
};

export default Shell;
